import React, { useCallback, useEffect, useState } from 'react';
import { FONT_MONO, FONT_SERIF } from '../landing/landingData';
import { domainsService, type DomainRow } from '../../services/domains.service';
import { RecordsTable, SmallBtn, StatusPill, fmtDate, payDomainOrder } from '../../components/studio/domainUi';
import { DangerNote, Spinner } from '../../components/studio/studioUi';
import { useConfirm } from '../../components/common/ConfirmDialog';
import { useToast } from '../../components/common/Toast';

/**
 * "Domains" panel on Credits & billing: every domain the user connected or
 * bought, its status and renewal date, with Renew (Midtrans) for domains bought
 * through HyVay and the DNS records for ones still waiting. Renewal reminder
 * emails link here.
 */
export const DomainsPanel: React.FC = () => {
    const [domains, setDomains] = useState<DomainRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState<string | null>(null);
    const [records, setRecords] = useState<{ id: string; rows: any[]; checks: any } | null>(null);
    const confirm = useConfirm();
    const toast = useToast();

    const load = useCallback(async () => {
        try {
            const r = await domainsService.list();
            setDomains(r.domains);
            setError(null);
        } catch (e: any) {
            setError(e?.message || 'Could not load domains.');
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { void load(); }, [load]);

    const renew = async (d: DomainRow) => {
        setBusy(d.id); setError(null);
        try {
            const start = await domainsService.renew(d.id, 1);
            const result = await payDomainOrder(start.token, start.order_id);
            if (result?.status === 'FULFILLED') toast.success(`${d.name} renewed until ${fmtDate(result.domain.expiresAt)}.`);
            else if (result?.status === 'NEEDS_ATTENTION') toast.warn('Payment received; the renewal is being completed by our team.', { label: 'PENDING' });
            await load();
        } catch (e: any) {
            setError(e.message);
        } finally {
            setBusy(null);
        }
    };

    const showRecords = async (d: DomainRow) => {
        const ins = await domainsService.instructions(d.id);
        setRecords({ id: d.id, rows: ins.records, checks: null });
    };

    const verify = async (d: DomainRow) => {
        setBusy(d.id); setError(null);
        try {
            const r = await domainsService.verify(d.id);
            setRecords(r.ok ? null : { id: d.id, rows: r.records, checks: r.checks });
            if (r.ok) toast.success(`${d.name} is connected.`);
            await load();
        } catch (e: any) {
            setError(e.message);
        } finally {
            setBusy(null);
        }
    };

    const detach = async (d: DomainRow) => {
        const external = d.source === 'EXTERNAL';
        const ok = await confirm({
            eyebrow: external ? 'DISCONNECT DOMAIN' : 'DETACH DOMAIN',
            title: <>{external ? 'Disconnect' : 'Detach'} <em>{d.name}</em>?</>,
            description: external
                ? 'Your site goes back to its hyvay.com address. You can connect the domain again later.'
                : 'The domain is detached from its project. You keep it and can attach it to a project again later.',
            confirmLabel: external ? 'Disconnect' : 'Detach',
            busyLabel: external ? 'Disconnecting…' : 'Detaching…',
            onConfirm: async () => { await domainsService.disconnect(d.id); },
        });
        if (!ok) return;
        toast.success(`${d.name} ${external ? 'disconnected' : 'detached'}.`);
        await load();
    };

    if (!loading && domains.length === 0 && !error) return null;

    return (
        <div className="hv-panel" style={{ padding: '26px 28px', borderRadius: 24, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 26px 50px -44px var(--hv-shadow)', animation: 'hvFade 0.45s ease both' }}>
            <div style={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.06em', color: 'var(--hv-mint-ink)' }}>DOMAINS</div>
            <div style={{ marginTop: 8, fontFamily: FONT_SERIF, fontSize: 26, lineHeight: 1.1, color: 'var(--hv-ink)' }}>Your domains</div>
            <p style={{ margin: '6px 0 0', fontSize: 13, lineHeight: 1.6, color: 'var(--hv-body)' }}>Domains bought through HyVay renew yearly, paid in rupiah. We email you 30, 7 and 1 days before a renewal is due.</p>

            {error && <DangerNote style={{ marginTop: 14 }}>{error}</DangerNote>}

            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {loading ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.06em', color: 'var(--hv-muted)' }}><Spinner size={12} /> LOADING…</div>
                ) : domains.map((d) => (
                    <React.Fragment key={d.id}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '12px 14px', borderRadius: 14, background: 'var(--hv-card-2)', border: '1px solid var(--hv-line)' }}>
                            <div style={{ flex: 1, minWidth: 180 }}>
                                <div style={{ fontFamily: FONT_MONO, fontSize: 13.5, color: 'var(--hv-ink)' }}>{d.name}</div>
                                <div style={{ marginTop: 3, fontSize: 11.5, color: 'var(--hv-muted)' }}>
                                    {d.applicationName ? `Project: ${d.applicationName}` : 'Not attached to a project'}
                                    {d.source === 'NAMESILO' ? (d.expiresAt ? ` · renews ${fmtDate(d.expiresAt)}` : ' · not registered yet') : ' · connected domain'}
                                </div>
                            </div>
                            <StatusPill domain={d} />
                            {d.source === 'NAMESILO' && (d.status === 'ACTIVE' || d.status === 'EXPIRED') && (
                                <SmallBtn onClick={() => void renew(d)} disabled={busy === d.id}>{busy === d.id ? <Spinner size={12} /> : null} Renew 1 year</SmallBtn>
                            )}
                            {d.status === 'PENDING_DNS' && (
                                <>
                                    <SmallBtn onClick={() => void showRecords(d)}>DNS records</SmallBtn>
                                    <SmallBtn onClick={() => void verify(d)} disabled={busy === d.id}>Check DNS</SmallBtn>
                                </>
                            )}
                            {d.applicationId && <SmallBtn danger onClick={() => void detach(d)}>Detach</SmallBtn>}
                        </div>
                        {records?.id === d.id && <RecordsTable records={records.rows} checks={records.checks} />}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
};
